import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { GripVertical, Pencil, Plus, Settings2, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
} from "@dg/ui";
import { useAuth } from "../contexts/auth.js";
import {
  getApps,
  getAdminSections,
  createAdminApp,
  updateAdminApp,
  deleteAdminApp,
  createAdminSection,
  updateAdminSection,
  deleteAdminSection,
  reorderAdminSections,
  reorderAdminApps,
  uploadAppFile,
  getAppFiles,
  deleteAppFile,
  restoreAppFileVersion,
  deleteAppFileVersion,
  isApiError,
  type App,
  type AppSection,
  type AppFile,
} from "../lib/api.js";
import { isValidRedirect } from "../lib/redirect.js";
import { AuthSpinner } from "../components/layout/auth-spinner.js";

// ── Brand icons ────────────────────────────────────────────────────────────

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
    </svg>
  );
}

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026c.462-.62.874-1.275 1.226-1.963.021-.04.001-.088-.041-.104a13.201 13.201 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z" />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function appTypeLabel(t: App["appType"]): string {
  if (t === "redirect") return "Redirect";
  return t === "file-display"
    ? "File Display"
    : t.charAt(0).toUpperCase() + t.slice(1);
}

function accessTypeBadgeClass(type: App["accessType"]): string {
  switch (type) {
    case "public":
      return "border-accent-teal text-accent-teal";
    case "private":
      return "border-accent-rose text-accent-rose";
    case "restricted":
      return "border-accent-gold text-accent-gold";
    case "hidden":
      return "border-text-muted text-text-muted";
  }
}

function appTypeBadgeClass(type: App["appType"]): string {
  switch (type) {
    case "hosted":
      return "border-accent-teal text-accent-teal";
    case "placeholder":
      return "border-text-muted text-text-muted";
    case "file-display":
      return "border-primary text-primary";
    case "redirect":
      return "border-accent-gold text-accent-gold";
  }
}

// ── App card ───────────────────────────────────────────────────────────────

type AppCardCallbacks = {
  onRename: (app: App) => void;
  onEdit: (app: App) => void;
  onToggleEnabled: (app: App) => void;
  onToggleDashboard: (app: App) => void;
  onDelete: (app: App) => void;
};

function AppCard({
  app,
  isAdmin,
  dragHandle,
  isDragging,
  callbacks,
  showBadges = true,
}: {
  app: App;
  isAdmin: boolean;
  dragHandle?: React.ReactNode;
  isDragging?: boolean;
  callbacks?: AppCardCallbacks;
  showBadges?: boolean;
}) {
  const href =
    app.appType === "redirect"
      ? (app.url ?? "#")
      : app.subdomainEnabled
        ? `https://${app.subdomain ?? app.slug}.jensen-dowell.me`
        : `/apps/${app.slug}`;

  return (
    <Card
      className={`flex flex-col gap-3 p-4 transition-shadow hover:shadow-md${isDragging ? " opacity-50" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {dragHandle}
          {app.iconUrl ? (
            <img
              src={app.iconUrl}
              alt=""
              className="h-8 w-8 rounded-md object-cover shrink-0"
            />
          ) : (
            <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center shrink-0 text-muted-foreground text-xs font-bold">
              {app.name[0]?.toUpperCase() ?? "A"}
            </div>
          )}
          <span
            className={`font-semibold ${showBadges ? "truncate" : "break-words"}`}
          >
            {app.name}
          </span>
        </div>

        <div className="flex items-start gap-1 shrink-0">
          {showBadges && (
            <div className="flex flex-col items-end gap-1">
              <Badge
                variant="outline"
                className={`capitalize text-xs ${accessTypeBadgeClass(app.accessType)}`}
              >
                {app.accessType}
              </Badge>
              <Badge
                variant="outline"
                className={`text-xs ${appTypeBadgeClass(app.appType)}`}
              >
                {appTypeLabel(app.appType)}
              </Badge>
              {isAdmin && !app.enabled && (
                <Badge
                  variant="outline"
                  className="text-xs text-muted-foreground"
                >
                  Offline
                </Badge>
              )}
            </div>
          )}
          {isAdmin && callbacks && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 -mt-0.5 shrink-0"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  <span className="sr-only">App settings</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => setTimeout(() => callbacks.onRename(app), 0)}
                >
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setTimeout(() => callbacks.onEdit(app), 0)}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => callbacks.onToggleEnabled(app)}
                >
                  {app.enabled ? "Disable" : "Enable"}
                </DropdownMenuItem>
                {app.accessType === "public" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="flex items-center justify-between"
                      onSelect={() => callbacks.onToggleDashboard(app)}
                    >
                      <span>Public Dashboard</span>
                      <Badge
                        variant="outline"
                        className={`ml-2 text-xs ${
                          app.showOnDashboard
                            ? "border-accent-teal text-accent-teal"
                            : "text-muted-foreground"
                        }`}
                      >
                        {app.showOnDashboard ? "On" : "Off"}
                      </Badge>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => setTimeout(() => callbacks.onDelete(app), 0)}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {app.description && (
        <p className="text-sm text-muted-foreground line-clamp-2">
          {app.description}
        </p>
      )}

      {isAdmin && (
        <div className="text-xs text-muted-foreground space-y-0.5">
          {app.port && <div>Port: {app.port}</div>}
          {app.passcodeType && <div>Passcode: {app.passcodeType}</div>}
        </div>
      )}

      <div className="mt-auto pt-1">
        <a
          href={href}
          className="inline-flex w-full items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 min-h-[44px]"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open
        </a>
      </div>
    </Card>
  );
}

// ── Sortable app card (admin DnD) ──────────────────────────────────────────

function SortableAppCard({
  app,
  callbacks,
}: {
  app: App;
  callbacks: AppCardCallbacks;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: app.id });

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    zIndex: isDragging ? 1 : undefined,
    position: "relative",
  };

  const dragHandle = (
    <button
      {...listeners}
      {...attributes}
      className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
      aria-label="Drag to reorder"
      type="button"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );

  return (
    <div ref={setNodeRef} style={style}>
      <AppCard
        app={app}
        isAdmin
        dragHandle={dragHandle}
        isDragging={isDragging}
        callbacks={callbacks}
      />
    </div>
  );
}

// ── Sortable section group (admin DnD) ─────────────────────────────────────

function SortableSectionGroup({
  section,
  children,
  onRename,
  onDelete,
}: {
  section: AppSection;
  children: React.ReactNode;
  onRename: (s: AppSection) => void;
  onDelete: (s: AppSection) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    zIndex: isDragging ? 1 : undefined,
    position: "relative",
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="space-y-3 mt-6">
      <div className="flex items-center gap-2">
        <button
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          aria-label="Drag section to reorder"
          type="button"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
          {section.name}
        </h2>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onRename(section)}
            aria-label="Rename section"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onDelete(section)}
            aria-label="Delete section"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {children}
    </div>
  );
}

// ── App grid skeleton ──────────────────────────────────────────────────────

function AppGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-40 rounded-lg" />
      ))}
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────

function Dashboard({ isAdmin }: { isAdmin: boolean }) {
  // ── Data ──────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const [localApps, setLocalApps] = useState<App[]>([]);
  const [localSections, setLocalSections] = useState<AppSection[]>([]);
  const [loading, setLoading] = useState(true);

  // ── DnD (admin only) ──────────────────────────────────────────────────────
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeDragType, setActiveDragType] = useState<
    "app" | "section" | null
  >(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // ── Create app dialog ─────────────────────────────────────────────────────
  const [showCreateApp, setShowCreateApp] = useState(false);
  const [createSlug, setCreateSlug] = useState("");
  const [createName, setCreateName] = useState("");
  const [createAccess, setCreateAccess] = useState("private");
  const [createAppType, setCreateAppType] = useState<App["appType"]>("hosted");
  const [createRedirectUrl, setCreateRedirectUrl] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createMaxFileSizeMb, setCreateMaxFileSizeMb] = useState(10);
  const [creating, setCreating] = useState(false);

  // ── Rename app dialog ─────────────────────────────────────────────────────
  const [renameApp, setRenameApp] = useState<App | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // ── Edit app dialog ───────────────────────────────────────────────────────
  const [editApp, setEditApp] = useState<App | null>(null);
  const [editSlug, setEditSlug] = useState("");
  const [editAccess, setEditAccess] = useState("private");
  const [editSubdomain, setEditSubdomain] = useState(false);
  const [editCustomSubdomain, setEditCustomSubdomain] = useState("");
  const [editVersioningEnabled, setEditVersioningEnabled] = useState(false);
  const [editMaxVersions, setEditMaxVersions] = useState(3);
  const [editMaxFileSizeMb, setEditMaxFileSizeMb] = useState(10);
  const [editDescription, setEditDescription] = useState("");
  const [editRedirectUrl, setEditRedirectUrl] = useState("");
  const [editing, setEditing] = useState(false);
  const [editFiles, setEditFiles] = useState<AppFile[] | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileActionLoading, setFileActionLoading] = useState<string | null>(
    null,
  );
  const [showVersioningWarning, setShowVersioningWarning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Delete app dialog ─────────────────────────────────────────────────────
  const [deleteApp, setDeleteApp] = useState<App | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  // ── Create section dialog ─────────────────────────────────────────────────
  const [showCreateSection, setShowCreateSection] = useState(false);
  const [createSectionName, setCreateSectionName] = useState("");
  const [creatingSection, setCreatingSection] = useState(false);

  // ── Rename section dialog ─────────────────────────────────────────────────
  const [renameSection, setRenameSection] = useState<AppSection | null>(null);
  const [renameSectionValue, setRenameSectionValue] = useState("");
  const [renamingSection, setRenamingSection] = useState(false);

  // ── Delete section dialog ─────────────────────────────────────────────────
  const [deleteSection, setDeleteSection] = useState<AppSection | null>(null);
  const [deletingSection, setDeletingSection] = useState(false);

  // ── Toggle enable/disable ─────────────────────────────────────────────────
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // ── Data loading ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const [appsRes, sectionsRes] = await Promise.all([
          getApps(),
          getAdminSections(),
        ]);
        setLocalApps(
          [...appsRes.data].sort((a, b) => a.sortOrder - b.sortOrder),
        );
        setLocalSections(
          [...sectionsRes.data].sort((a, b) => a.sortOrder - b.sortOrder),
        );
      } else {
        const res = await getApps();
        setLocalApps([...res.data].sort((a, b) => a.sortOrder - b.sortOrder));
        setLocalSections(
          [...res.sections].sort((a, b) => a.sortOrder - b.sortOrder),
        );
      }
    } catch {
      toast.error("Failed to load apps.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── App callbacks ─────────────────────────────────────────────────────────

  function openEdit(app: App) {
    setEditApp(app);
    setEditSlug(app.slug);
    setEditAccess(app.accessType);
    setEditSubdomain(app.subdomainEnabled);
    setEditCustomSubdomain(app.subdomain ?? "");
    setEditVersioningEnabled(app.versioningEnabled);
    setEditMaxVersions(app.maxVersions ?? 3);
    setEditMaxFileSizeMb(app.maxFileSizeMb);
    setEditDescription(app.description ?? "");
    setEditRedirectUrl(app.url ?? "");
    if (app.appType === "file-display") {
      void loadEditFiles(app.slug);
    } else {
      setEditFiles(null);
    }
  }

  const callbacks: AppCardCallbacks = {
    onRename: (app) => {
      setRenameApp(app);
      setRenameValue(app.name);
    },
    onEdit: openEdit,
    onToggleEnabled: (app) => void toggleEnabled(app),
    onToggleDashboard: (app) => void toggleDashboard(app),
    onDelete: (app) => {
      setDeleteApp(app);
      setDeleteConfirm("");
    },
  };

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (
      (createAppType !== "redirect" && !createSlug.trim()) ||
      !createName.trim()
    )
      return;
    setCreating(true);
    try {
      await createAdminApp({
        ...(createAppType !== "redirect" ? { slug: createSlug.trim() } : {}),
        name: createName.trim(),
        ...(createDescription.trim()
          ? { description: createDescription.trim() }
          : {}),
        accessType: createAccess,
        appType: createAppType,
        ...(createAppType === "redirect"
          ? { url: createRedirectUrl.trim() }
          : {}),
        ...(createAppType === "file-display"
          ? { maxFileSizeMb: createMaxFileSizeMb }
          : {}),
      });
      toast.success("App created.");
      setShowCreateApp(false);
      setCreateSlug("");
      setCreateName("");
      setCreateDescription("");
      setCreateAppType("hosted");
      setCreateRedirectUrl("");
      setCreateMaxFileSizeMb(10);
      await load();
    } catch (err) {
      toast.error(
        isApiError(err) ? "Failed to create app." : "Something went wrong.",
      );
    } finally {
      setCreating(false);
    }
  }

  async function handleRename(e: React.FormEvent) {
    e.preventDefault();
    if (!renameApp || !renameValue.trim()) return;
    setRenaming(true);
    try {
      await updateAdminApp(renameApp.id, { name: renameValue.trim() });
      toast.success("App renamed.");
      setRenameApp(null);
      await load();
    } catch {
      toast.error("Failed to rename app.");
    } finally {
      setRenaming(false);
    }
  }

  async function loadEditFiles(slug: string) {
    setEditFiles(null);
    try {
      const res = await getAppFiles(slug);
      setEditFiles(res.data);
    } catch {
      toast.error("Failed to load files.");
      setEditFiles([]);
    }
  }

  async function saveEdit() {
    if (!editApp) return;
    setEditing(true);
    try {
      await updateAdminApp(editApp.id, {
        ...(editSlug !== editApp.slug && editApp.appType !== "redirect"
          ? { slug: editSlug }
          : {}),
        description: editDescription.trim() || null,
        accessType: editAccess,
        ...(editApp.appType !== "redirect"
          ? {
              subdomainEnabled: editSubdomain,
              subdomain: editSubdomain
                ? editCustomSubdomain.trim() || null
                : null,
            }
          : {}),
        ...(editApp.appType === "redirect"
          ? { url: editRedirectUrl.trim() }
          : {}),
        ...(editApp.appType === "file-display"
          ? {
              versioningEnabled: editVersioningEnabled,
              maxVersions: editVersioningEnabled ? editMaxVersions : null,
              maxFileSizeMb: editMaxFileSizeMb,
            }
          : {}),
      });
      toast.success("App updated.");
      setEditApp(null);
      setEditFiles(null);
      await load();
    } catch {
      toast.error("Failed to update app.");
    } finally {
      setEditing(false);
    }
  }

  async function handleEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editApp) return;
    if (
      editApp.versioningEnabled &&
      !editVersioningEnabled &&
      editFiles &&
      editFiles.length > 1
    ) {
      setShowVersioningWarning(true);
      return;
    }
    await saveEdit();
  }

  async function handleFileUpload(file: File) {
    if (!editApp) return;
    setFileUploading(true);
    try {
      await uploadAppFile(editApp.slug, file);
      toast.success("File uploaded.");
      const res = await getAppFiles(editApp.slug);
      setEditFiles(res.data);
    } catch (err) {
      toast.error(isApiError(err) ? "Upload failed." : "Something went wrong.");
    } finally {
      setFileUploading(false);
    }
  }

  async function handleDeleteAllFiles() {
    if (!editApp) return;
    setFileActionLoading("all");
    try {
      await deleteAppFile(editApp.slug);
      toast.success("File deleted.");
      setEditFiles([]);
    } catch {
      toast.error("Failed to delete file.");
    } finally {
      setFileActionLoading(null);
    }
  }

  async function handleRestoreVersion(fileId: string) {
    if (!editApp) return;
    setFileActionLoading(fileId);
    try {
      await restoreAppFileVersion(editApp.slug, fileId);
      toast.success("Version restored.");
      const res = await getAppFiles(editApp.slug);
      setEditFiles(res.data);
    } catch {
      toast.error("Failed to restore version.");
    } finally {
      setFileActionLoading(null);
    }
  }

  async function handleDeleteVersion(fileId: string) {
    if (!editApp) return;
    setFileActionLoading(fileId);
    try {
      await deleteAppFileVersion(editApp.slug, fileId);
      toast.success("Version deleted.");
      const res = await getAppFiles(editApp.slug);
      setEditFiles(res.data);
    } catch {
      toast.error("Failed to delete version.");
    } finally {
      setFileActionLoading(null);
    }
  }

  async function handleDeleteApp() {
    if (!deleteApp) return;
    setDeleting(true);
    try {
      await deleteAdminApp(deleteApp.id);
      toast.success("App deleted.");
      setDeleteApp(null);
      setDeleteConfirm("");
      await load();
    } catch {
      toast.error("Failed to delete app.");
    } finally {
      setDeleting(false);
    }
  }

  async function toggleEnabled(app: App) {
    setActionLoading(app.id);
    try {
      await updateAdminApp(app.id, { enabled: !app.enabled });
      toast.success(app.enabled ? "App disabled." : "App enabled.");
      await load();
    } catch {
      toast.error("Action failed.");
    } finally {
      setActionLoading(null);
    }
  }

  async function toggleDashboard(app: App) {
    setActionLoading(app.id);
    try {
      await updateAdminApp(app.id, { showOnDashboard: !app.showOnDashboard });
      toast.success(
        app.showOnDashboard
          ? "Removed from public dashboard."
          : "Added to public dashboard.",
      );
      await load();
    } catch {
      toast.error("Action failed.");
    } finally {
      setActionLoading(null);
    }
  }

  // ── Section callbacks ─────────────────────────────────────────────────────

  async function handleCreateSection(e: React.FormEvent) {
    e.preventDefault();
    if (!createSectionName.trim()) return;
    setCreatingSection(true);
    try {
      await createAdminSection(createSectionName.trim());
      toast.success("Section created.");
      setShowCreateSection(false);
      setCreateSectionName("");
      await load();
    } catch {
      toast.error("Failed to create section.");
    } finally {
      setCreatingSection(false);
    }
  }

  async function handleRenameSection(e: React.FormEvent) {
    e.preventDefault();
    if (!renameSection || !renameSectionValue.trim()) return;
    setRenamingSection(true);
    try {
      await updateAdminSection(renameSection.id, renameSectionValue.trim());
      toast.success("Section renamed.");
      setRenameSection(null);
      await load();
    } catch {
      toast.error("Failed to rename section.");
    } finally {
      setRenamingSection(false);
    }
  }

  async function handleDeleteSection() {
    if (!deleteSection) return;
    setDeletingSection(true);
    try {
      await deleteAdminSection(deleteSection.id);
      toast.success("Section deleted. Apps moved to unsectioned.");
      setDeleteSection(null);
      await load();
    } catch {
      toast.error("Failed to delete section.");
    } finally {
      setDeletingSection(false);
    }
  }

  // ── DnD handlers ──────────────────────────────────────────────────────────

  function handleDragStart({ active }: DragStartEvent) {
    const id = String(active.id);
    setActiveId(id);
    setActiveDragType(
      localSections.some((s) => s.id === id) ? "section" : "app",
    );
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    if (!over) return;
    const activeAppId = String(active.id);
    const overId = String(over.id);

    if (localSections.some((s) => s.id === activeAppId)) return;

    const activeApp = localApps.find((a) => a.id === activeAppId);
    if (!activeApp) return;

    const activeContainer = activeApp.sectionId;
    const overApp = localApps.find((a) => a.id === overId);
    const overSection = localSections.find((s) => s.id === overId);
    const targetContainer = overApp
      ? overApp.sectionId
      : overSection
        ? overSection.id
        : null;

    if (activeContainer === targetContainer) return;

    setLocalApps((apps) => {
      const appsInTarget = apps.filter((a) => a.sectionId === targetContainer);
      const newSortOrder = overApp ? overApp.sortOrder : appsInTarget.length;
      return apps.map((a) =>
        a.id === activeAppId
          ? { ...a, sectionId: targetContainer, sortOrder: newSortOrder }
          : a,
      );
    });
  }

  async function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveId(null);
    setActiveDragType(null);
    if (!over || active.id === over.id) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    const isSection = localSections.some((s) => s.id === activeIdStr);

    if (isSection) {
      const sorted = [...localSections].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      );
      const oldIdx = sorted.findIndex((s) => s.id === activeIdStr);
      const newIdx = sorted.findIndex((s) => s.id === overIdStr);
      if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;

      const newSections = arrayMove(sorted, oldIdx, newIdx).map((s, i) => ({
        ...s,
        sortOrder: i,
      }));
      const prevSections = localSections;
      setLocalSections(newSections);

      try {
        await reorderAdminSections(
          newSections.map((s) => ({ id: s.id, sortOrder: s.sortOrder })),
        );
      } catch {
        toast.error("Failed to save section order.");
        setLocalSections(prevSections);
      }
    } else {
      const activeApp = localApps.find((a) => a.id === activeIdStr);
      if (!activeApp) return;

      const overApp = localApps.find((a) => a.id === overIdStr);
      const overSection = localSections.find((s) => s.id === overIdStr);
      const targetContainer = overApp
        ? overApp.sectionId
        : overSection
          ? overSection.id
          : activeApp.sectionId;

      const appsInTarget = localApps
        .filter((a) => a.sectionId === targetContainer)
        .sort((a, b) => a.sortOrder - b.sortOrder);

      let newApps: App[];

      if (overApp && overApp.sectionId === targetContainer) {
        const oldIdx = appsInTarget.findIndex((a) => a.id === activeIdStr);
        const newIdx = appsInTarget.findIndex((a) => a.id === overIdStr);
        if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) {
          newApps = localApps;
        } else {
          const reordered = arrayMove(appsInTarget, oldIdx, newIdx).map(
            (a, i) => ({ ...a, sortOrder: i }),
          );
          const appMap = new Map(reordered.map((a) => [a.id, a]));
          newApps = localApps.map((a) => appMap.get(a.id) ?? a);
        }
      } else {
        const withoutActive = appsInTarget.filter((a) => a.id !== activeIdStr);
        const newSortOrder = withoutActive.length;
        newApps = localApps.map((a) =>
          a.id === activeIdStr
            ? { ...a, sectionId: targetContainer, sortOrder: newSortOrder }
            : a,
        );
      }

      const prevApps = localApps;
      setLocalApps(newApps);

      try {
        await reorderAdminApps(
          newApps.map((a) => ({
            id: a.id,
            sortOrder: a.sortOrder,
            sectionId: a.sectionId,
          })),
        );
      } catch {
        toast.error("Failed to save app order.");
        setLocalApps(prevApps);
      }
    }
  }

  // ── Derived data ───────────────────────────────────────────────────────────

  const sortedSections = [...localSections].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const unsectionedApps = localApps
    .filter((a) => a.sectionId === null)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const deleteAppTarget = `Apps/${deleteApp?.name ?? ""}`;

  const activeApp = activeId ? localApps.find((a) => a.id === activeId) : null;
  const activeSection = activeId
    ? localSections.find((s) => s.id === activeId)
    : null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-1.5 min-h-[44px]">
                <Plus className="h-4 w-4" />
                Create
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => setTimeout(() => setShowCreateApp(true), 0)}
              >
                New app
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setTimeout(() => setShowCreateSection(true), 0)}
              >
                New section
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate("/admin/connectors")}>
                Connectors
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <AppGridSkeleton />
      ) : isAdmin ? (
        // ── Admin DnD layout ────────────────────────────────────────────────
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={(e) => void handleDragEnd(e)}
        >
          <SortableContext
            items={sortedSections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {/* Unsectioned apps — no header */}
            {unsectionedApps.length > 0 && (
              <div className="mb-4">
                <SortableContext
                  items={unsectionedApps.map((a) => a.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {unsectionedApps.map((app) => (
                      <SortableAppCard
                        key={app.id}
                        app={app}
                        callbacks={callbacks}
                      />
                    ))}
                  </div>
                </SortableContext>
              </div>
            )}

            {/* Sections */}
            {sortedSections.map((section) => {
              const sectionApps = localApps
                .filter((a) => a.sectionId === section.id)
                .sort((a, b) => a.sortOrder - b.sortOrder);

              return (
                <SortableSectionGroup
                  key={section.id}
                  section={section}
                  onRename={(s) => {
                    setRenameSection(s);
                    setRenameSectionValue(s.name);
                  }}
                  onDelete={setDeleteSection}
                >
                  <SortableContext
                    items={sectionApps.map((a) => a.id)}
                    strategy={rectSortingStrategy}
                  >
                    {sectionApps.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {sectionApps.map((app) => (
                          <SortableAppCard
                            key={app.id}
                            app={app}
                            callbacks={callbacks}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
                        Empty section — drag apps here.
                      </p>
                    )}
                  </SortableContext>
                </SortableSectionGroup>
              );
            })}
          </SortableContext>

          {unsectionedApps.length === 0 && sortedSections.length === 0 && (
            <p className="text-muted-foreground py-8 text-center">
              No apps yet. Use Create to add your first app.
            </p>
          )}

          <DragOverlay>
            {activeDragType === "app" && activeApp && (
              <div className="rotate-1 opacity-95">
                <AppCard app={activeApp} isAdmin />
              </div>
            )}
            {activeDragType === "section" && activeSection && (
              <div className="flex items-center gap-2 py-2 px-3 bg-card border border-border rounded-lg shadow-lg">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  {activeSection.name}
                </span>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        // ── Non-admin static layout ─────────────────────────────────────────
        <div className="space-y-6">
          {unsectionedApps.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {unsectionedApps.map((app) => (
                <AppCard key={app.id} app={app} isAdmin={false} />
              ))}
            </div>
          )}

          {sortedSections.map((section) => {
            const sectionApps = localApps
              .filter((a) => a.sectionId === section.id)
              .sort((a, b) => a.sortOrder - b.sortOrder);
            if (sectionApps.length === 0) return null;
            return (
              <div key={section.id} className="space-y-3">
                <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  {section.name}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {sectionApps.map((app) => (
                    <AppCard key={app.id} app={app} isAdmin={false} />
                  ))}
                </div>
              </div>
            );
          })}

          {localApps.length === 0 && (
            <p className="text-muted-foreground py-8 text-center">
              No apps to display.
            </p>
          )}
        </div>
      )}

      {/* ── Dialogs ───────────────────────────────────────────────────────── */}

      {/* Create app — modal={false} for Select portal compatibility */}
      <Dialog
        modal={false}
        open={showCreateApp}
        onOpenChange={setShowCreateApp}
      >
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>New app</DialogTitle>
            <DialogDescription>
              Register a new app on the platform.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => void handleCreate(e)} className="space-y-4">
            {createAppType !== "redirect" && (
              <div className="space-y-1.5">
                <Label htmlFor="app-slug">Slug</Label>
                <Input
                  id="app-slug"
                  placeholder="my-app"
                  pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
                  value={createSlug}
                  onChange={(e) => setCreateSlug(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Lowercase letters, numbers, hyphens only.
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="app-name">Name</Label>
              <Input
                id="app-name"
                placeholder="My App"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="app-description">
                Description{" "}
                <span className="text-muted-foreground text-xs font-normal">
                  (optional)
                </span>
              </Label>
              <textarea
                id="app-description"
                className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                placeholder="Short description shown on the app card"
                maxLength={500}
                rows={2}
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>App type</Label>
              <Select
                value={createAppType}
                onValueChange={(v) => setCreateAppType(v as App["appType"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hosted">Hosted</SelectItem>
                  <SelectItem value="placeholder">Placeholder</SelectItem>
                  <SelectItem value="file-display">File Display</SelectItem>
                  <SelectItem value="redirect">Redirect</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {createAppType === "hosted" && "Reverse-proxied app process."}
                {createAppType === "placeholder" && "Coming-soon holding page."}
                {createAppType === "file-display" &&
                  "Platform renders an uploaded file."}
                {createAppType === "redirect" &&
                  "Redirects visitors to an external URL."}
              </p>
            </div>
            {createAppType === "redirect" && (
              <div className="space-y-1.5">
                <Label htmlFor="create-redirect-url">Redirect URL</Label>
                <Input
                  id="create-redirect-url"
                  type="url"
                  placeholder="https://example.com"
                  value={createRedirectUrl}
                  onChange={(e) => setCreateRedirectUrl(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Must be an HTTPS URL.
                </p>
              </div>
            )}
            {createAppType === "file-display" && (
              <div className="space-y-1.5">
                <Label htmlFor="create-max-file-size">Max file size (MB)</Label>
                <Input
                  id="create-max-file-size"
                  type="number"
                  min={1}
                  max={50}
                  value={createMaxFileSizeMb}
                  onChange={(e) =>
                    setCreateMaxFileSizeMb(Number(e.target.value))
                  }
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Access type</Label>
              <Select value={createAccess} onValueChange={setCreateAccess}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateApp(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? "Creating…" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit app — modal={false} for Select portal compatibility */}
      <Dialog
        modal={false}
        open={editApp !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditApp(null);
            setEditFiles(null);
            setShowVersioningWarning(false);
            setEditSlug("");
            setEditCustomSubdomain("");
          }
        }}
      >
        <DialogContent
          className="max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Edit app</DialogTitle>
            <DialogDescription>{editApp?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => void handleEdit(e)} className="space-y-4">
            {editApp?.appType !== "redirect" && (
              <div className="space-y-1.5">
                <Label htmlFor="edit-slug">Slug</Label>
                <Input
                  id="edit-slug"
                  value={editSlug}
                  onChange={(e) =>
                    setEditSlug(
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                    )
                  }
                  pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
                  maxLength={64}
                  required
                />
                {editSlug !== editApp?.slug && (
                  <p className="text-xs text-accent-gold">
                    Changing the slug breaks existing links and Caddy routes.
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="edit-description">
                Description{" "}
                <span className="text-muted-foreground text-xs font-normal">
                  (optional)
                </span>
              </Label>
              <textarea
                id="edit-description"
                className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                placeholder="Short description shown on the app card"
                maxLength={500}
                rows={2}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Access type</Label>
              <Select value={editAccess} onValueChange={setEditAccess}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editApp?.appType !== "redirect" && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    id="edit-subdomain"
                    type="checkbox"
                    checked={editSubdomain}
                    onChange={(e) => setEditSubdomain(e.target.checked)}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <Label htmlFor="edit-subdomain" className="cursor-pointer">
                    Enable subdomain (
                    <code className="text-xs">
                      {editCustomSubdomain.trim() || editSlug || editApp?.slug}
                      .jensen-dowell.me
                    </code>
                    )
                  </Label>
                </div>
                {editSubdomain && (
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-custom-subdomain">
                      Custom subdomain (optional)
                    </Label>
                    <Input
                      id="edit-custom-subdomain"
                      value={editCustomSubdomain}
                      onChange={(e) =>
                        setEditCustomSubdomain(
                          e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, ""),
                        )
                      }
                      placeholder={editSlug || editApp?.slug}
                      pattern="^[a-z0-9]+(-[a-z0-9]+)*$|^$"
                      maxLength={63}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave blank to use the slug as subdomain.
                    </p>
                  </div>
                )}
              </div>
            )}
            {editApp?.appType === "redirect" && (
              <div className="space-y-1.5">
                <Label htmlFor="edit-redirect-url">Redirect URL</Label>
                <Input
                  id="edit-redirect-url"
                  type="url"
                  placeholder="https://example.com"
                  value={editRedirectUrl}
                  onChange={(e) => setEditRedirectUrl(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Must be an HTTPS URL.
                </p>
              </div>
            )}
            {editApp?.appType === "file-display" && (
              <>
                <Separator />
                <div className="space-y-3">
                  <p className="text-sm font-medium">File settings</p>
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-max-file-size">
                      Max file size (MB)
                    </Label>
                    <Input
                      id="edit-max-file-size"
                      type="number"
                      min={1}
                      max={50}
                      value={editMaxFileSizeMb}
                      onChange={(e) =>
                        setEditMaxFileSizeMb(Number(e.target.value))
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="edit-versioning"
                      type="checkbox"
                      checked={editVersioningEnabled}
                      onChange={(e) =>
                        setEditVersioningEnabled(e.target.checked)
                      }
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <Label htmlFor="edit-versioning" className="cursor-pointer">
                      Enable versioning
                    </Label>
                  </div>
                  {editVersioningEnabled && (
                    <div className="space-y-1.5">
                      <Label htmlFor="edit-max-versions">Max versions</Label>
                      <Input
                        id="edit-max-versions"
                        type="number"
                        min={2}
                        max={10}
                        value={editMaxVersions}
                        onChange={(e) =>
                          setEditMaxVersions(Number(e.target.value))
                        }
                      />
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditApp(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={editing}>
                {editing ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>

          {/* File section — outside the form so file actions don't trigger form submit */}
          {editApp?.appType === "file-display" && (
            <>
              <Separator />
              <div className="space-y-3">
                <p className="text-sm font-medium">File</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".md,.txt,.html,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleFileUpload(file);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={fileUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {fileUploading
                      ? "Uploading…"
                      : editFiles?.some((f) => f.isActive)
                        ? "Replace file"
                        : "Upload file"}
                  </Button>
                  {editFiles?.some((f) => f.isActive) && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={fileUploading || fileActionLoading !== null}
                      onClick={() => void handleDeleteAllFiles()}
                    >
                      Delete file
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Accepted: .md, .txt, .html, .pdf · Max {editMaxFileSizeMb} MB
                </p>

                {editFiles === null ? (
                  <Skeleton className="h-12 rounded" />
                ) : (
                  <>
                    {(() => {
                      const active = editFiles.find((f) => f.isActive);
                      return active ? (
                        <div className="p-3 bg-muted rounded-lg text-sm">
                          <div className="font-medium">{active.filename}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {formatFileSize(active.fileSize)} ·{" "}
                            {active.fileType} · Uploaded{" "}
                            {formatDate(active.uploadedAt)}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">
                          No file uploaded yet.
                        </p>
                      );
                    })()}

                    {editFiles.length > 1 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Version history
                        </p>
                        {editFiles.map((f) => (
                          <Card
                            key={f.id}
                            className={`p-2${f.isActive ? " ring-1 ring-primary/30" : ""}`}
                          >
                            <div className="flex items-center gap-2 justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm truncate">
                                  {f.filename}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  v{f.version} · {formatFileSize(f.fileSize)} ·{" "}
                                  {formatDate(f.uploadedAt)}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {f.isActive ? (
                                  <Badge variant="default" className="text-xs">
                                    Active
                                  </Badge>
                                ) : (
                                  <>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      disabled={fileActionLoading === f.id}
                                      onClick={() =>
                                        void handleRestoreVersion(f.id)
                                      }
                                    >
                                      Restore
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="destructive"
                                      disabled={fileActionLoading === f.id}
                                      onClick={() =>
                                        void handleDeleteVersion(f.id)
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Versioning warning */}
      <Dialog
        open={showVersioningWarning}
        onOpenChange={setShowVersioningWarning}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable versioning?</DialogTitle>
            <DialogDescription>
              All versions except the active file will be permanently deleted.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowVersioningWarning(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowVersioningWarning(false);
                void saveEdit();
              }}
            >
              Disable versioning
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename app */}
      <Dialog
        open={renameApp !== null}
        onOpenChange={(open) => !open && setRenameApp(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename app</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => void handleRename(e)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rename-value">New name</Label>
              <Input
                id="rename-value"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                maxLength={100}
                required
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameApp(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={renaming}>
                {renaming ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete app */}
      <Dialog
        open={deleteApp !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteApp(null);
            setDeleteConfirm("");
          }
        }}
      >
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Delete app</DialogTitle>
            <DialogDescription>
              This removes the app registration from the platform. Files
              deployed on the server are not deleted. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Type{" "}
            <code className="bg-muted px-1 py-0.5 rounded text-foreground">
              {deleteAppTarget}
            </code>{" "}
            to confirm.
          </p>
          <Input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={deleteAppTarget}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-1">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteApp(null);
                setDeleteConfirm("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteConfirm !== deleteAppTarget || deleting}
              onClick={() => void handleDeleteApp()}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create section */}
      <Dialog
        open={showCreateSection}
        onOpenChange={(open) => {
          setShowCreateSection(open);
          if (!open) setCreateSectionName("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New section</DialogTitle>
            <DialogDescription>
              Add a section to organise your apps.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => void handleCreateSection(e)}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="section-name">Name</Label>
              <Input
                id="section-name"
                placeholder="e.g. Tools, Projects…"
                value={createSectionName}
                onChange={(e) => setCreateSectionName(e.target.value)}
                maxLength={100}
                required
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateSection(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creatingSection}>
                {creatingSection ? "Creating…" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename section */}
      <Dialog
        open={renameSection !== null}
        onOpenChange={(open) => !open && setRenameSection(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename section</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => void handleRenameSection(e)}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="rename-section-value">Name</Label>
              <Input
                id="rename-section-value"
                value={renameSectionValue}
                onChange={(e) => setRenameSectionValue(e.target.value)}
                maxLength={100}
                required
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameSection(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={renamingSection}>
                {renamingSection ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete section */}
      <Dialog
        open={deleteSection !== null}
        onOpenChange={(open) => !open && setDeleteSection(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete section</DialogTitle>
            <DialogDescription>
              The section will be removed. All apps in this section will move to
              unsectioned. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteSection(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deletingSection}
              onClick={() => void handleDeleteSection()}
            >
              {deletingSection ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suppress unused warning for actionLoading — used in callbacks */}
      {actionLoading && null}
    </div>
  );
}

// ── Public dashboard page ──────────────────────────────────────────────────

function PublicDashboardPage({
  onSignIn,
}: {
  onSignIn: (provider: "github" | "discord" | "linkedin") => Promise<void>;
}) {
  const [loginOpen, setLoginOpen] = useState(false);
  const [apps, setApps] = useState<App[]>([]);
  const [sections, setSections] = useState<AppSection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const res = await getApps();
        setApps([...res.data].sort((a, b) => a.sortOrder - b.sortOrder));
        setSections(
          [...res.sections].sort((a, b) => a.sortOrder - b.sortOrder),
        );
      } catch {
        // public page — silently show empty state
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sortedSections = [...sections].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const unsectionedApps = apps
    .filter((a) => a.sectionId === null)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">
          Freyja&apos;s Digital Garden
        </h1>
        <Button className="min-h-[44px]" onClick={() => setLoginOpen(true)}>
          Login
        </Button>
      </div>

      {loading ? (
        <AppGridSkeleton />
      ) : (
        <div className="space-y-6">
          {unsectionedApps.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {unsectionedApps.map((app) => (
                <AppCard
                  key={app.id}
                  app={app}
                  isAdmin={false}
                  showBadges={false}
                />
              ))}
            </div>
          )}

          {sortedSections.map((section) => {
            const sectionApps = apps
              .filter((a) => a.sectionId === section.id)
              .sort((a, b) => a.sortOrder - b.sortOrder);
            if (sectionApps.length === 0) return null;
            return (
              <div key={section.id} className="space-y-3">
                <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  {section.name}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {sectionApps.map((app) => (
                    <AppCard
                      key={app.id}
                      app={app}
                      isAdmin={false}
                      showBadges={false}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {apps.length === 0 && (
            <p className="text-muted-foreground py-8 text-center">
              No apps to display.
            </p>
          )}
        </div>
      )}

      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign in</DialogTitle>
            <DialogDescription>
              Sign in to access all apps and tools.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Button
              className="gap-2 w-full min-h-[44px]"
              onClick={() => void onSignIn("github")}
            >
              <GitHubIcon className="h-5 w-5" />
              Sign in with GitHub
            </Button>
            <Button
              variant="outline"
              className="gap-2 w-full min-h-[44px]"
              onClick={() => void onSignIn("discord")}
            >
              <DiscordIcon className="h-5 w-5" />
              Sign in with Discord
            </Button>
            <Button
              variant="outline"
              className="gap-2 w-full min-h-[44px]"
              onClick={() => void onSignIn("linkedin")}
            >
              <LinkedInIcon className="h-5 w-5" />
              Sign in with LinkedIn
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center pt-2">
            By signing in you consent to collection of your OAuth profile (name,
            email, avatar) and usage logs for platform operation. See{" "}
            <a href="/privacy" className="underline hover:text-foreground">
              Privacy Notice
            </a>
            .
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Pending / Rejected pages ───────────────────────────────────────────────

function PendingPage() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      <div className="text-4xl font-bold text-primary">◆</div>
      <h1 className="text-2xl font-bold">Account pending approval</h1>
      <p className="text-muted-foreground max-w-sm">
        Your account is awaiting approval from an admin. You'll receive access
        once approved.
      </p>
      <p className="text-sm text-muted-foreground">
        Contact{" "}
        <a
          href="mailto:admin@jensen-dowell.me"
          className="underline hover:text-foreground"
        >
          admin@jensen-dowell.me
        </a>{" "}
        if you have questions.
      </p>
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────

export function HomePage() {
  const { auth, signIn } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (auth.status !== "active") return;
    const redirectTo = searchParams.get("redirect");
    if (redirectTo && isValidRedirect(redirectTo)) {
      window.location.href = redirectTo;
    }
  }, [auth.status, searchParams]);

  if (auth.status === "loading") return <AuthSpinner />;
  if (auth.status === "unauthenticated")
    return <PublicDashboardPage onSignIn={signIn} />;
  if (auth.status === "pending") return <PendingPage />;

  return <Dashboard isAdmin={auth.user.role === "admin"} />;
}
